import { LanguageSelect } from "@/components/dashboard/StyleControls";
import { Flame } from "lucide-react";
import { Input } from "@/components/ui/input";
import { RIZZ_CONTROLS, RIZZ_STYLES } from "@/lib/config";

export const RizzToggle = ({ on, onToggle }) => (
  <button data-testid="rizz-mode-toggle" aria-pressed={on} onClick={onToggle}
    className={`w-full h-14 rounded-3xl font-display font-extrabold text-lg inline-flex items-center justify-center gap-2 transition-all duration-300 ${on ? "bg-gradient-to-r from-orange-500 via-pink-500 to-fuchsia-600 text-white shadow-lg shadow-pink-500/30" : "border-2 border-dashed border-pink-500/50 text-pink-500 hover:bg-pink-500/10"}`}>
    <Flame className={`h-5 w-5 ${on ? "animate-pulse" : ""}`} /> Rizz Mode 🔥 <span className="text-xs font-semibold opacity-80">{on ? "ON" : "OFF"}</span>
  </button>
);

const Segmented = ({ label, value, options, onChange, testKey }) => (
  <div className="space-y-1.5">
    <div className="text-xs font-semibold text-muted-foreground">{label}</div>
    <div className="flex flex-wrap gap-1 rounded-2xl bg-secondary/50 p-1">
      {options.map(([id, name]) => (
        <button key={id} data-testid={`rizz-${testKey}-${id}`} onClick={() => onChange(id)}
          className={`flex-1 min-w-[64px] rounded-xl py-1.5 text-xs font-semibold transition-colors ${value === id ? "bg-gradient-to-r from-orange-500 to-pink-500 text-white" : "text-muted-foreground hover:text-foreground"}`}>
          {name}
        </button>
      ))}
    </div>
  </div>
);

export const RizzControls = ({ rizz, setRizz }) => {
  const set = (k) => (v) => setRizz((r) => {
    if (k !== "style") return { ...r, [k]: v };
    if (v === "hinglish") return { ...r, style: v, language: "hinglish", prevLanguage: r.style === "hinglish" ? r.prevLanguage : r.language };
    if (r.style === "hinglish") return { ...r, style: v, language: r.prevLanguage || "english", prevLanguage: undefined };
    return { ...r, style: v };
  });
  const active = RIZZ_STYLES.find((s) => s.id === rizz.style);
  return (
    <div className="panel p-5 sm:p-6 space-y-5 ring-1 ring-pink-500/30" data-testid="rizz-controls">
      <div className="space-y-2">
        <div className="text-sm font-semibold">Rizz style</div>
        <div className="grid grid-cols-2 sm:grid-cols-5 lg:grid-cols-2 xl:grid-cols-5 gap-1.5">
          {RIZZ_STYLES.map((s) => (
            <button key={s.id} data-testid={`rizz-style-${s.id}`} onClick={() => set("style")(s.id)} title={s.desc}
              className={`rounded-xl border px-2 py-2 text-[11px] font-semibold transition-colors ${rizz.style === s.id ? "border-pink-500 bg-pink-500/10 text-foreground" : "border-border text-muted-foreground hover:border-pink-500/40"}`}>
              {s.name}
            </button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground" data-testid="rizz-style-description">{active?.desc}</p>
        {rizz.style === "custom" && (
          <Input data-testid="rizz-custom-tone-input" value={rizz.custom_tone} maxLength={150} onChange={(e) => set("custom_tone")(e.target.value)}
            placeholder="e.g. mysterious, a little sarcastic, Bollywood references" className="h-11 rounded-xl" />
        )}
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <div className="text-xs font-semibold text-muted-foreground">Language</div>
          <LanguageSelect value={rizz.language} onChange={set("language")} testId="rizz-language-select" withAuto={false} />
        </div>
        {RIZZ_CONTROLS.filter((c) => c.key !== "language").map((c) => (
          <div key={c.key} className={c.key === "personality" ? "sm:col-span-2" : ""}>
            <Segmented label={c.label} value={rizz[c.key]} options={c.options} onChange={set(c.key)} testKey={c.key} />
          </div>
        ))}
      </div>
      <p className="text-[11px] text-muted-foreground">Rizz stays respectful — if they've said no, you'll get a graceful reply instead.</p>
    </div>
  );
};
